import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spin, Select, Typography } from 'antd';
import { LandUseLandCoverMap, NDVIVegetationIndicatorMap } from '../map/LandscapeMap';
import { LatLngTuple } from 'leaflet';
import CyprusLandUseLandCover from '../../assets/images/cyprus_land_use_land_cover.png';
import GreeceThraceLandUseLandCover from '../../assets/images/greece_thrake_land_use_land_cover.png';
import GreeceEviaLandUseLandCover from '../../assets/images/greece_evia_land_use_land_cover.png';
import Cookies from 'js-cookie';
import { CaseStudyKey } from '../../interfaces';

const { Option } = Select;

const LoadingScreen: React.FC = () => (
  <div style={{ textAlign: 'center', marginTop: '50px' }}>
    <Spin size='large' />
    <p>Loading landscape data...</p>
  </div>
);

/////////////////////////////////
//  Land Use & Land Cover Map  //
/////////////////////////////////

type MapAccuracyData = {
  category: string;
  accuracy: {
    [year: string]: number | string;
  };
};

export type LandUseAndLandCoverLayer = {
  title: string;
  leftLayer: string;
  leftTitle: string;
  rightTitle: string;
  rightLayer: string;
  center: LatLngTuple;
  zoom: number;
  statisticalAnalysis: string;
  mapAccuracy: MapAccuracyData[];
  wmsUrl: string;
  legend: { color: string; label: string }[];
};

type LandUseAndCoverData = {
  layers?: LandUseAndLandCoverLayer[];
};

const LAND_USE_AND_LAND_COVER_DATA: Record<CaseStudyKey, LandUseAndCoverData> = {
  cyprus: {
    layers: [
      {
        title: 'Larnaca',
        leftLayer: 'cite:Cyprus_LULC_2015',
        leftTitle: 'Year 2015',
        rightTitle: 'Year 2024',
        rightLayer: 'cite:Cyprus_LULC_2024',
        center: [34.726, 33.303] as LatLngTuple,
        zoom: 14,
        statisticalAnalysis: CyprusLandUseLandCover,
        mapAccuracy: [
          { category: 'Water', accuracy: { '2015 Map Accuracy': 1.0, '2023 Map Accuracy': 1.0 } },
          { category: 'Developed', accuracy: { '2015 Map Accuracy': 0.928, '2023 Map Accuracy': 0.933 } },
          { category: 'Shrubland', accuracy: { '2015 Map Accuracy': 0.93, '2023 Map Accuracy': 0.893 } },
          { category: 'Cultivated', accuracy: { '2015 Map Accuracy': 0.959, '2023 Map Accuracy': 0.887 } },
          { category: 'Total accuracy', accuracy: { '2015 Map Accuracy': 0.965, '2023 Map Accuracy': 0.94 } },
          { category: 'Kappa coefficient', accuracy: { '2015 Map Accuracy': 0.951, '2023 Map Accuracy': 0.917 } },
        ],
        wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms',
        legend: [
          { color: '#5475a8', label: 'Water' },
          { color: '#ffbebe', label: 'Developed' },
          { color: '#af963c', label: 'Shrubland' },
          { color: '#fbf65d', label: 'Cultivated' },
        ],
      },
    ],
  },

  greece: {
    layers: [
      {
        title: 'Thrace',
        leftLayer: 'cite:Thrake_LULC_2015',
        leftTitle: 'Year 2015',
        rightTitle: 'Year 2023',
        rightLayer: 'cite:Thrake_LULC_2023',
        center: [41.055, 25.93] as LatLngTuple,
        zoom: 13,
        statisticalAnalysis: GreeceThraceLandUseLandCover,
        mapAccuracy: [
          { category: 'Developed', accuracy: { '2015 Map Accuracy': 0.861, '2023 Map Accuracy': 0.871 } },
          { category: 'Trees', accuracy: { '2015 Map Accuracy': 0.95, '2023 Map Accuracy': 0.973 } },
          { category: 'Shrubland', accuracy: { '2015 Map Accuracy': 0.875, '2023 Map Accuracy': 0.82 } },
          { category: 'Planted/Cultivated', accuracy: { '2015 Map Accuracy': 0.923, '2023 Map Accuracy': 0.879 } },
          { category: 'Burned', accuracy: { '2015 Map Accuracy': '-', '2023 Map Accuracy': 0.934 } },
          { category: 'Total Accuracy', accuracy: { '2015 Map Accuracy': 0.938, '2023 Map Accuracy': 0.934 } },
          { category: 'Kappa coefficient', accuracy: { '2015 Map Accuracy': 0.811, '2023 Map Accuracy': 0.898 } },
        ],
        wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms',
        legend: [
          { color: '#ffbebe', label: 'Developed' },
          { color: '#85c77e', label: 'Trees' },
          { color: '#af963c', label: 'Shrubland' },
          { color: '#fbf65d', label: 'Planted/Cultivated' },
          { color: '#de3170', label: 'Burned' },
        ],
      },
      {
        title: 'Evia',
        leftLayer: 'cite:Evia_LULC_2015',
        leftTitle: 'Year 2015',
        rightTitle: 'Year 2023',
        rightLayer: 'cite:Evia_LULC_2023',
        center: [38.086, 24.54] as LatLngTuple,
        zoom: 13,
        statisticalAnalysis: GreeceEviaLandUseLandCover,
        mapAccuracy: [
          { category: 'Developed', accuracy: { '2015 Map Accuracy': 0.957, '2023 Map Accuracy': 0.934 } },
          { category: 'Barren', accuracy: { '2015 Map Accuracy': 0.941, '2023 Map Accuracy': 0.903 } },
          { category: 'Trees', accuracy: { '2015 Map Accuracy': 0.88, '2023 Map Accuracy': 0.866 } },
          {
            category: 'Shrubland/Herbaceous vegetation',
            accuracy: { '2015 Map Accuracy': 0.957, '2023 Map Accuracy': 0.978 },
          },
          { category: 'Planted/Cultivated', accuracy: { '2015 Map Accuracy': 0.647, '2023 Map Accuracy': 0.688 } },
          { category: 'Total Accuracy', accuracy: { '2015 Map Accuracy': 0.931, '2023 Map Accuracy': 0.937 } },
          { category: 'Kappa coefficient', accuracy: { '2015 Map Accuracy': 0.868, '2023 Map Accuracy': 0.889 } },
        ],
        wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms',
        legend: [
          { color: '#ffbebe', label: 'Developed' },
          { color: '#dbdbdb', label: 'Barren' },
          { color: '#85c77e', label: 'Trees' },
          { color: '#af963c', label: 'Shrubland/Herbaceous vegetation' },
          { color: '#fbf65d', label: 'Planted/Cultivated' },
        ],
      },
    ],
  },

  barcelona: {
    layers: undefined,
  },
  pnr_narbonnaise: {
    layers: undefined,
  },
};

const LandUseLandCover: React.FC = () => {
  const caseStudy = Cookies.get('caseStudy');
  const [loading, setLoading] = useState<boolean>(!caseStudy);
  const [selectedLayer, setSelectedLayer] = useState<LandUseAndLandCoverLayer | undefined>(undefined);

  useEffect(() => {
    setLoading(!caseStudy);
    if (caseStudy) {
      const landscapeData = LAND_USE_AND_LAND_COVER_DATA[caseStudy as CaseStudyKey];
      if (landscapeData && landscapeData.layers && landscapeData.layers.length > 0) {
        setSelectedLayer(landscapeData.layers[0]);
      }
    }
  }, [caseStudy]);

  if (loading || !caseStudy) return <LoadingScreen />;

  const landscapeData = LAND_USE_AND_LAND_COVER_DATA[caseStudy as CaseStudyKey];

  return (
    <div>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>
        Home &gt; Landscape &gt; Land Use and Land Cover
      </h1>
      {landscapeData.layers && landscapeData.layers.length >= 2 && (
        <Card>
          <Row align='middle'>
            <Col>
              <h3 style={{ marginRight: '10px' }}>Select a Place:</h3>
            </Col>
            <Col flex='auto'>
              <Select
                id='landUseCoverSelectAreaId'
                value={selectedLayer?.title}
                onChange={(value) => {
                  const layer = landscapeData.layers?.find((layer) => layer.title === value);
                  setSelectedLayer(layer);
                }}
                style={{ width: '10%' }}
                placeholder='Choose a layer'
              >
                {landscapeData.layers?.map((layer) => (
                  <Option key={layer.title} value={layer.title}>
                    {layer.title}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>
      )}
      {/* Map Card */}
      <Card
        title={
          selectedLayer && (
            <Row gutter={16}>
              <Col span={12}>
                <h3 style={{ textAlign: 'center' }}>{selectedLayer.leftTitle}</h3>
              </Col>
              <Col span={12}>
                <h3 style={{ textAlign: 'center' }}>{selectedLayer.rightTitle}</h3>
              </Col>
            </Row>
          )
        }
      >
        {selectedLayer && <LandUseLandCoverMap layer={selectedLayer} />}
        <Typography.Paragraph style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Learn more about Landscape Dynamics:<Typography.Link href='https://guarden.org/technologies/landscape-dynamics-mapping/' target='_blank'> https://guarden.org/technologies/landscape-dynamics-mapping/</Typography.Link>
        </Typography.Paragraph>
      </Card>

      {/* Statistical Analysis Card */}
      {selectedLayer?.statisticalAnalysis && (
        <Card
          style={{ marginTop: '20px' }}
          title={<h3>Statistical Analysis</h3>}
          id='landUseCoverStatisticalAnalysisId'
        >
          <img
            src={selectedLayer.statisticalAnalysis}
            alt='Statistical Analysis'
            style={{ width: '100%', height: 'auto' }}
          />
        </Card>
      )}
      {/* Map Accuracy Card */}
      {selectedLayer?.mapAccuracy && (
        <Card
          style={{ marginTop: '20px' }}
          title={<h3>Map Accuracy</h3>}
          id='landUseCoverMapAccuracyId'
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Category</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>2015 Map Accuracy</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>2023 Map Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {selectedLayer.mapAccuracy.map((row, index) => {
                // Find the corresponding legend entry for the category
                const legendEntry = selectedLayer.legend?.find(
                  (legend) => legend.label.toLowerCase() === row.category.toLowerCase(),
                );

                // Set the background color for the entire row
                const categoryColor = legendEntry ? legendEntry.color : '#f2f2f2'; // Custom fallback color for unmatched categories

                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: categoryColor, // Apply background color for the entire row
                      color: 'black', // Optional: adjust text color for contrast if you want the text to be white on dark colors
                    }}
                  >
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.category}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      {row.accuracy['2015 Map Accuracy'] !== undefined ? row.accuracy['2015 Map Accuracy'] : 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      {row.accuracy['2023 Map Accuracy'] !== undefined ? row.accuracy['2023 Map Accuracy'] : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Map Accuracy Explanation Card */}
      <Card
        title={<h3 style={{ textAlign: 'left' }}>Map Accuracy Table Explanation</h3>}
        style={{ marginTop: '20px' }}
      >
        <div style={{ padding: '15px' }}>
          <p>The Map Accuracy Table shows how well each land use/land cover category was classified compared to reality on the ground.</p>

          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '10px' }}>
            <li><strong>Accuracy Values:</strong> Numbers close to 1 (or 100%) mean higher confidence, while lower values indicate more uncertainty.</li>
            <li><strong>Total Accuracy:</strong> Shows how much of the map is correct overall.</li>
            <li><strong>Kappa Value:</strong> Confirms that the results are very reliable and not just a matter of chance. Values above 0.8 indicate strong reliability.</li>
          </ul>

          <p style={{ marginTop: '15px' }}>
            <strong>Understanding the Scores:</strong>
          </p>

          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '5px' }}>
            <li>The closer the number is to 1 (or 100%), the better. For example, 0.95 means the map was correct 95 times out of 100 for that category.</li>
            <li>Each category shows how well it was recognized: Some land types (like trees/forests) are easier to detect and get higher scores, while others (like shrubland) are more difficult and may score lower.</li>
            <li>Total Accuracy shows overall trust in the map: Values above 90% mean the map is highly reliable for most uses.</li>
            <li>The Kappa value double-checks reliability: Values above 0.8 mean the map's agreement with reality is very strong.</li>
            <li>Comparing years shows changes in reliability: If a category's score goes down (e.g., shrubland from 2015 to 2023), it means that land type was harder to identify in the newer map.</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

/////////////////////////////////////
//  NDVI Vegetation Indicator Map  //
/////////////////////////////////////

type NDVILayer = {
  title: string;
  name: string;
  wmsUrl: string;
};

export type NDVIVegetationIndicatorData = {
  title: string;
  layers: NDVILayer[];
  center: LatLngTuple;
  zoom: number;
};

const NDVI_VEGETATION_INDICATOR_DATA: Record<CaseStudyKey, NDVIVegetationIndicatorData[]> = {
  cyprus: [
    {
      title: 'Cyprus',
      layers: [
        { title: 'Year 1990', name: 'Cyprus_NDVI_1990', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
        { title: 'Year 2000', name: 'Cyprus_NDVI_2000', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
        { title: 'Year 2010', name: 'Cyprus_NDVI_2010', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
      ],
      center: [34.726, 33.303] as LatLngTuple,
      zoom: 13,
    },
  ],
  greece: [
    {
      title: 'Thrace',
      layers: [
        { title: 'Year 2015', name: 'Thrake_NDVI_2015', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
        { title: 'Year 2023', name: 'Thrake_NDVI_2023', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
      ],
      center: [41.055, 25.93] as LatLngTuple,
      zoom: 12,
    },
    {
      title: 'Evia',
      layers: [
        { title: 'Year 2015', name: 'Evia_NDVI_2015', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
        { title: 'Year 2023', name: 'Evia_NDVI_2023', wmsUrl: 'https://landdynamics.draxis.gr/geoserver/cite/wms' },
      ],
      center: [38.086, 24.54] as LatLngTuple,
      zoom: 13,
    },
  ],
  barcelona: [],
  pnr_narbonnaise: [],
};

const NdviIndicator: React.FC = () => {
  const caseStudy = Cookies.get('caseStudy');
  const [loading, setLoading] = useState<boolean>(!caseStudy);
  const [selectedLayer, setSelectedLayer] = useState<NDVIVegetationIndicatorData | undefined>(undefined);
  const [selectedLayers, setSelectedLayers] = useState<[string, string] | null>(null);

  useEffect(() => {
    setLoading(!caseStudy);
    if (caseStudy) {
      const landscapeData = NDVI_VEGETATION_INDICATOR_DATA[caseStudy as CaseStudyKey];
      if (landscapeData && landscapeData.length > 0) {
        // Initialize with the first layer from the selected case study
        setSelectedLayer(landscapeData[0]);
      }
    }
  }, [caseStudy]);

  const handleLayerChange = (value: string) => {
    if (caseStudy) {
      const landscapeData = NDVI_VEGETATION_INDICATOR_DATA[caseStudy as CaseStudyKey];
      const selected = landscapeData.find((layer) => layer.title === value);
      setSelectedLayer(selected);
    }
  };

  if (loading || !caseStudy) return <LoadingScreen />;

  const landscapeData = NDVI_VEGETATION_INDICATOR_DATA[caseStudy as CaseStudyKey];

  const handleLayerSelection = ([title1, title2]: [string, string]) => {
    // Only update selected layers if they are different from the current ones
    if (!selectedLayers || selectedLayers[0] !== title1 || selectedLayers[1] !== title2) {
      setSelectedLayers([title1, title2]);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '10px', color: '#329619', fontSize: '25px' }}>
        Home &gt; Landscape &gt; NDVI Vegetation Indicator
      </h1>

      {landscapeData.length >= 2 && (
        <Card>
          <Row align='middle'>
            <Col>
              <h3 style={{ marginRight: '10px' }}>Select a Place:</h3>
            </Col>
            <Col flex='auto'>
              <Select
                id='ndviSelectAreaId'
                style={{ width: '10%' }}
                placeholder='Select a Place'
                onChange={handleLayerChange}
                value={selectedLayer?.title}
              >
                {landscapeData.map((layer) => (
                  <Option key={layer.title} value={layer.title}>
                    {layer.title}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>
      )}

      {/* Map Card */}
      <Card
        title={
          selectedLayer && selectedLayers ? (
            <Row gutter={16}>
              <Col span={12}>
                <h3 style={{ textAlign: 'center' }}>{selectedLayers[0]}</h3>
              </Col>
              <Col span={12}>
                <h3 style={{ textAlign: 'center' }}>{selectedLayers[1]}</h3>
              </Col>
            </Row>
          ) : (
            <h3 style={{ textAlign: 'center' }}>Select a Layer</h3>
          )
        }
      >
        {selectedLayer && (
          <>
            <NDVIVegetationIndicatorMap
              data={selectedLayer}
              onLayerPairSelect={handleLayerSelection}
              legend={landscapeData[0].layers.map((layer, idx) => ({
                color: '#329619',
                label: layer.title,
              }))}
            />
            <Typography.Paragraph style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Learn more about Landscape Dynamics:<Typography.Link href='https://guarden.org/technologies/landscape-dynamics-mapping/' target='_blank'> https://guarden.org/technologies/landscape-dynamics-mapping/</Typography.Link>
            </Typography.Paragraph>
          </>
        )}
      </Card>
    </div>
  );
};

export { LandUseLandCover, NdviIndicator };
